import { Dialog } from '@headlessui/react'
import { useEffect, useRef, useState } from 'react'
import type { EnhancedAuditScope, EnhancedProjectType } from '../../../interfaces/reports-tab/enhanced-narrative'
import { useGetEnhancedNarrativeReportMutation } from '../../../store/chatbox/chatboxApi'
import { describeEnhancedNarrativeResponse, normalizeEnhancedNarrativeResponse, parseEnhancedNarrativeReport, type EnhancedReportDocument } from '../../../utils/reports/enhancedNarrative'
import EnhancedReportConfigModal from './EnhancedReportConfigModal'
import ReportPreparingModal from './ReportPreparingModal'
import ReportEnhancedNarrativePreviewModal from './ReportEnhancedNarrativePreviewModal'

export type EnhancedReportConfig = { clientId: string; propertyId: string; selectedLabel?: string; auditScope?: EnhancedAuditScope; projectType?: EnhancedProjectType }
const waitingSteps = [{ id: 'generate', label: 'Researching and preparing your enhanced report' }]
const errorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message
    const value = error as { data?: { message?: string | string[] }; error?: string }
    const message = value?.data?.message
    return (Array.isArray(message) ? message.join(' ') : message) || value?.error || 'We could not generate the enhanced report. Please try again.'
}

const EnhancedNarrativeReportFlow = ({ onClose }: { onClose: () => void }) => {
    const [fetchReport] = useGetEnhancedNarrativeReportMutation()
    const [step, setStep] = useState<'configure' | 'preparing' | 'preview' | 'error'>('configure')
    const [config, setConfig] = useState<EnhancedReportConfig | null>(null)
    const [document, setDocument] = useState<EnhancedReportDocument | null>(null)
    const [error, setError] = useState('')
    const [isRegenerating, setIsRegenerating] = useState(false)
    const request = useRef<ReturnType<typeof fetchReport> | null>(null)
    const generation = useRef(0)

    useEffect(() => () => {
        generation.current += 1
        request.current?.abort()
    }, [])

    const close = () => {
        generation.current += 1
        request.current?.abort()
        request.current = null
        onClose()
    }
    const generate = async (values: EnhancedReportConfig, regenerate = false) => {
        if (!values.propertyId.trim() || request.current) return
        setConfig(values)
        if (!regenerate) setDocument(null)
        setError('')
        if (regenerate) setIsRegenerating(true)
        else setStep('preparing')
        const current = ++generation.current
        const pending = fetchReport({
            property: values.propertyId,
            ...(values.auditScope ? { auditScope: values.auditScope } : {}),
            ...(values.projectType ? { projectType: values.projectType } : {}),
            ...(regenerate ? { regenerate: true } : {}),
        })
        request.current = pending
        try {
            const response = await pending.unwrap()
            if (generation.current !== current) return
            const report = normalizeEnhancedNarrativeResponse(response)
            if (!report) throw new Error(`The AI service returned an incomplete report (no slides in the response; received: ${describeEnhancedNarrativeResponse(response)}). Please try again.`)
            setDocument(parseEnhancedNarrativeReport(report, values.selectedLabel || 'Property'))
            setStep('preview')
        } catch (failure) {
            if (generation.current !== current) return
            setError(errorMessage(failure))
            if (!regenerate) setStep('error')
        } finally {
            if (generation.current === current) {
                request.current = null
                setIsRegenerating(false)
            }
        }
    }
    if (step === 'configure') return <EnhancedReportConfigModal onClose={close} initialValues={config} onContinue={generate} />
    if (step === 'preparing') return <ReportPreparingModal isOpen onClose={close} reportTitle='Enhanced Report' reportIcon='/assets/reports/narrative.svg' steps={waitingSteps} description='This in-depth report can take several minutes. Keep this window open. Leo will show your report as soon as it is ready.' />
    if (step === 'preview' && document) return <ReportEnhancedNarrativePreviewModal document={document} onClose={close} isRegenerating={isRegenerating} regenerationError={error} onRegenerate={() => config && generate(config, true)} />
    return (
        <Dialog open onClose={close} className='relative z-[130]'>
            <div className='fixed inset-0 bg-black/40 backdrop-blur-[2px]' aria-hidden='true' />
            <div className='fixed inset-0 flex items-center justify-center p-4'>
                <Dialog.Panel className='w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-[#09090b]'>
                    <Dialog.Title className='text-xl font-semibold text-slate-900 dark:text-slate-50'>Enhanced report could not be generated</Dialog.Title>
                    <p role='alert' className='mt-3 text-sm text-slate-600 dark:text-slate-300'>{error}</p>
                    <div className='mt-6 flex flex-wrap gap-3'>
                        <button type='button' onClick={() => config && generate(config)} className='rounded-lg bg-[#004b16] px-4 py-2 text-sm font-medium text-white'>Try again</button>
                        <button type='button' onClick={() => setStep('configure')} className='rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-slate-700 dark:text-white'>Change filters</button>
                        <button type='button' onClick={close} className='rounded-lg px-4 py-2 text-sm text-slate-600 dark:text-slate-300'>Close</button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default EnhancedNarrativeReportFlow
